import supabase from "../../../../../utils/supabase";

const TAG = "[scientificNameService]";

/**
 * Server-side fetching and processing for scientific names using the dedicated view.
 * @param {function} normalize - The normalization function to apply to each row.
 */
export async function fetchScientificNames({
  page,
  pageSize,
  orderBy,
  ascending,
  search,
  normalize,
}) {
  console.log(`${TAG} fetch start`, { page, pageSize, orderBy, ascending, search });
  const from = page * pageSize;
  const to = from + pageSize - 1;

  const orderMap = {
    id: "id",
    name_spell_valid: "name_spell_valid",
    name_spell_original: "name_spell_original",
    authority: "authority_year", // Proxy for sorting
    authority_year: "authority_year",
    current_rank: "current_rank",
    current_parent: "current_parent_name",
    original_rank: "original_rank",
    original_parent: "original_parent_name",
    valid_name_id: "valid_name_id",
    type_taxa_id: "type_taxa_id",
    extant_fossil: "extant_fossil",
    type_category: "type_category",
    type_locality: "type_locality",
    type_sex: "type_sex",
    type_repository: "type_repository_display",
    type_host: "type_host",
    source_of_original_description: "source_of_original_description",
    page: "page",
    remark: "remark",
    last_update: "last_update",
    status: "valid_name_id",
    children_count: "children_count",
  };
  const orderColumn = orderMap[orderBy] || "name_spell_valid";

  let query = supabase
    .from("view_for_scientific_name_service")
    .select("*", { count: "exact" });

  // If a search term is provided, apply a broad OR filter.
  if (search) {
    const safeSearch = `%${search.trim().replace(/[%_]/g, "\\$&")}%`;
    const searchNumber = Number(search.trim());

    const orClauses = [
      `name_spell_valid.ilike.${safeSearch}`,
      `name_spell_original.ilike.${safeSearch}`,
      `current_rank.ilike.${safeSearch}`,
      `original_rank.ilike.${safeSearch}`,
      `type_category.ilike.${safeSearch}`,
      `type_locality.ilike.${safeSearch}`,
      `type_sex.ilike.${safeSearch}`,
      `type_host.ilike.${safeSearch}`,
      `remark.ilike.${safeSearch}`,
      // Also search the joined fields from the view
      `current_parent_name.ilike.${safeSearch}`,
      `original_parent_name.ilike.${safeSearch}`,
      `type_repository_display.ilike.${safeSearch}`,
    ];

    if (!Number.isNaN(searchNumber)) {
      orClauses.push(`authority_year.eq.${searchNumber}`);
    }

    query = query.or(orClauses.join(","));
  }

  query = query.range(from, to).order(orderColumn, { ascending });

  const { data, error, count } = await query;

  if (error) {
    console.error(`${TAG} fetch error`, error);
    return { rows: [], total: 0 };
  }

  const rows = Array.isArray(data) ? data : [];

  const synonymKeywords = ["synonym", "junior synonym", "objective synonym", "subjective synonym"];

  const mapped = rows.map((r) => {
    const rankLower = (r.current_rank || "").toString().toLowerCase();
    const isSynonymByRank = synonymKeywords.some((kw) => rankLower === kw);
    const isSynonymByLink = !!(r.valid_name_id && r.valid_name_id !== r.id);

    // The data from the view is now the single source of truth.
    // We pass it to the normalizer which creates the final display properties.
    return normalize({
      ...r,
      _nameValidDisp: r.name_spell_valid,
      _nameOrigDisp: r.name_spell_original,
      _is_synonym: isSynonymByRank || isSynonymByLink,
      _validName: r.valid_name_str || "",
      _typeRepo: r.type_repository_display || r.type_repository || "",
      _remark: r.remark || "",
      _childCount: r.children_count || 0,
      _currentParentName: r.current_parent_name || r.current_parent || "",
      _originalParentName: r.original_parent_name || r.original_parent || "",
    });
  });

  return { rows: mapped, total: typeof count === "number" ? count : mapped.length };
}
